import React from 'react';
import { makeFluencyIcon } from './FluencyIcon';
import { getAssetPath } from '../utils/assetPath';
import { cn } from '../lib/utils';

type AppIconProps = {
  size?: number | string;
  className?: string;
};

type IconStatus = 'available' | 'missing';

const iconStatusCache = new Map<string, IconStatus>();
const iconLoadCache = new Map<string, Promise<IconStatus>>();

function loadIconStatus(src: string): Promise<IconStatus> {
  const cachedStatus = iconStatusCache.get(src);
  if (cachedStatus) {
    return Promise.resolve(cachedStatus);
  }

  const cachedPromise = iconLoadCache.get(src);
  if (cachedPromise) {
    return cachedPromise;
  }

  const promise = new Promise<IconStatus>((resolve) => {
    const image = new Image();

    image.onload = () => {
      iconStatusCache.set(src, 'available');
      iconLoadCache.delete(src);
      resolve('available');
    };

    image.onerror = () => {
      iconStatusCache.set(src, 'missing');
      iconLoadCache.delete(src);
      resolve('missing');
    };

    image.src = src;
  });

  iconLoadCache.set(src, promise);
  return promise;
}

function makeAssetIcon(assetId: string, FallbackIcon: React.ComponentType<AppIconProps>) {
  const AssetIcon: React.FC<AppIconProps> = ({ size = 18, className }) => {
    const src = React.useMemo(
      () => getAssetPath(`/os/assets/icons/${assetId}.svg`),
      [assetId]
    );
    const [status, setStatus] = React.useState<IconStatus | 'loading'>(() => iconStatusCache.get(src) || 'loading');

    React.useEffect(() => {
      if (status !== 'loading') {
        return;
      }

      let isMounted = true;

      loadIconStatus(src).then((nextStatus) => {
        if (isMounted) {
          setStatus(nextStatus);
        }
      });

      return () => {
        isMounted = false;
      };
    }, [src, status]);

    if (status !== 'available') {
      return <FallbackIcon size={size} className={className} />;
    }

    return (
      <img
        src={src}
        alt={assetId}
        width={size}
        height={size}
        draggable={false}
        className={cn('inline-block shrink-0 object-contain', className)}
        onError={() => {
          iconStatusCache.set(src, 'missing');
          setStatus('missing');
        }}
      />
    );
  };

  AssetIcon.displayName = `AssetIcon(${assetId})`;

  return AssetIcon;
}

export function makeProgramIcon(programId: string, fallbackSlug = 'document') {
  return makeAssetIcon(programId, makeFluencyIcon(fallbackSlug, programId));
}

export function makeOverrideIcon(assetId: string, fallbackIcon: React.ComponentType<AppIconProps>) {
  return makeAssetIcon(assetId, fallbackIcon);
}